import { motion, useReducedMotion } from "framer-motion";
import { Reveal, Rise } from "./Reveal";
import HeritageFlyers from "./HeritageFlyers";
import content from "../content";

// Paleta tomada del logo "Un Solo Pueblo".
const RED = "#D84830";
const GOLD = "#F0C060";
const CREAM = "#F5ECD8";
const BLUE = "#3048A8";

// Borde de banderas latinoamericanas (marquesina infinita).
const FlagMarquee = ({ flags, reverse = false, reduced }) => {
  const doubled = [...flags, ...flags];
  return (
    <div className="w-full overflow-hidden bg-[#0b0503]/55 backdrop-blur-sm ring-1 ring-white/10 py-1.5">
      <motion.div
        className="flex w-max items-center"
        animate={reduced ? {} : { x: reverse ? ["-50%", "0%"] : ["0%", "-50%"] }}
        transition={reduced ? {} : { duration: 55, repeat: Infinity, ease: "linear" }}
      >
        {doubled.map((f, i) => (
          <img
            key={`${f.code}-${i}`}
            src={`/images/flags/${f.code}.svg`}
            alt=""
            loading="lazy"
            className="mr-3 h-4 md:h-5 w-auto rounded-[2px] ring-1 ring-black/20 shadow-[0_1px_3px_rgba(0,0,0,0.55)]"
          />
        ))}
      </motion.div>
    </div>
  );
};

export default function HeritageSection() {
  const h = content.heritage;
  const reduced = useReducedMotion();
  if (!h || h.hidden) return null;

  return (
    <section
      id="herencia"
      className="relative w-full overflow-hidden py-28 md:py-40 bg-[#0d0604]"
      data-testid="heritage-section"
    >
      {/* Fondo: el mismo video, desenfocado, atenuado y sin sonido, en bucle */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {h.video && (
          <video
            className="absolute inset-0 w-full h-full object-cover opacity-[0.45] scale-125 select-none"
            style={{ filter: "blur(22px)" }}
            src={h.video.src}
            poster={h.video.poster}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            data-testid="heritage-bg-video"
          />
        )}
        {/* Velo cálido para que el texto sea legible */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1f0e08]/80 via-[#140a06]/78 to-[#0b0503]/90" />
        <div
          className="absolute left-1/2 top-[32%] -translate-x-1/2 -translate-y-1/2 w-[92vw] h-[92vw] max-w-[920px] max-h-[920px] rounded-full opacity-60"
          style={{
            background:
              "radial-gradient(circle, rgba(216,72,48,0.28), rgba(240,192,96,0.10) 46%, transparent 70%)",
          }}
        />
      </div>

      {/* Borde de banderas latinoamericanas: arriba y abajo */}
      {h.flags?.length > 0 && (
        <>
          <div className="pointer-events-none absolute inset-x-0 top-0 z-[2]" aria-hidden="true">
            <FlagMarquee flags={h.flags} reduced={reduced} />
          </div>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[2]" aria-hidden="true">
            <FlagMarquee flags={h.flags} reverse reduced={reduced} />
          </div>
        </>
      )}

      <div className="relative z-[3] w-full max-w-4xl mx-auto px-6 text-center">
        <Reveal>
          <Rise>
            <p
              className="font-poppins text-xs md:text-sm font-semibold tracking-[0.3em] uppercase mb-3"
              style={{ color: GOLD }}
            >
              {h.eyebrow}
            </p>
          </Rise>
          <Rise>
            <p
              className="font-poppins text-[11px] md:text-xs tracking-[0.28em] uppercase mb-12"
              style={{ color: CREAM, opacity: 0.75 }}
            >
              {h.dateRange}
            </p>
          </Rise>
          <Rise>
            <motion.img
              src={h.logo}
              alt={h.logoAlt}
              className="mx-auto w-[min(80vw,540px)] drop-shadow-[0_20px_50px_rgba(0,0,0,0.6)]"
              animate={{ y: [0, -12, 0], rotate: [-1.4, 1.4, -1.4] }}
              transition={{ duration: 7.5, repeat: Infinity, ease: "easeInOut" }}
              data-testid="heritage-logo"
            />
          </Rise>
        </Reveal>

        <Reveal className="mt-14">
          <Rise>
            <blockquote
              className="font-poppins italic text-lg md:text-2xl leading-relaxed max-w-3xl mx-auto [text-shadow:0_2px_18px_rgba(0,0,0,0.7)]"
              style={{ color: CREAM }}
              data-testid="heritage-verse"
            >
              &ldquo;{h.verse.text}&rdquo;
            </blockquote>
          </Rise>
          <Rise>
            <p
              className="mt-6 font-poppins text-sm md:text-base tracking-[0.22em] uppercase font-semibold"
              style={{ color: GOLD }}
            >
              {h.verse.reference}
            </p>
          </Rise>
          <Rise>
            <p
              className="mt-10 font-poppins text-base md:text-lg leading-relaxed max-w-2xl mx-auto [text-shadow:0_2px_14px_rgba(0,0,0,0.6)]"
              style={{ color: CREAM, opacity: 0.85 }}
              data-testid="heritage-body"
            >
              {h.body}
            </p>
          </Rise>
        </Reveal>

        {h.video && (
          <Reveal className="mt-16">
            <Rise>
              <p
                className="mb-6 font-poppins text-xs md:text-sm font-semibold tracking-[0.3em] uppercase"
                style={{ color: GOLD }}
              >
                {h.video.label}
              </p>
            </Rise>
            <Rise>
              <div
                className="mx-auto w-[min(80vw,340px)] rounded-[26px] p-[3px] shadow-2xl"
                style={{ background: `linear-gradient(145deg, ${GOLD}, ${RED} 55%, ${BLUE})` }}
              >
                <video
                  className="block w-full rounded-[23px] bg-black"
                  src={h.video.src}
                  poster={h.video.poster}
                  controls
                  playsInline
                  preload="metadata"
                  data-testid="heritage-video"
                />
              </div>
            </Rise>
          </Reveal>
        )}

        <HeritageFlyers />
      </div>
    </section>
  );
}
